import React from "react";
import { fetchMissingZips, fetchAbandonedCheckouts, fetchBookingStarts, deleteBookingStart } from "../../../util/settings_api_util";

// Owner-only: unserved zip searches and abandoned checkouts.
const styles = {
  tabs: { display: "flex", gap: "8px", marginTop: "16px", marginBottom: "16px", flexWrap: "wrap" },
  tab: { margin: 0, height: "40px", padding: "0 18px" },
  filters: { display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center", marginBottom: "12px" },
  scroll: { overflowX: "auto", WebkitOverflowScrolling: "touch" },
  table: { width: "100%", minWidth: "720px", borderCollapse: "collapse" },
  th: { textAlign: "left", padding: "10px 12px", borderBottom: "2px solid #e2e2e2", fontWeight: 600, whiteSpace: "nowrap", cursor: "pointer" },
  td: { textAlign: "left", padding: "10px 12px", borderBottom: "1px solid #efefef", verticalAlign: "top" },
  num: { textAlign: "right" },
  pill: (bg, fg) => ({ display: "inline-block", padding: "2px 8px", borderRadius: "10px", background: bg, color: fg, fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap" }),
  summary: { display: "flex", gap: "24px", flexWrap: "wrap", margin: "4px 0 16px", color: "#525a6c" },
  message: { padding: "16px 4px", color: "#777" }
};

// All times shown in Eastern.
const fmtDate = (v) => v ? `${new Date(v).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })} ET` : "—";
const money = (v) => `$${Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

export default class DemandInsights extends React.Component {
  constructor(props) {
    super(props);
    this.state = { view: "zips", zips: [], checkouts: [], starts: [], hideBookedStarts: false, loading: true, error: false, search: "", hideBooked: true, zipSort: "searches" };
  }

  componentDidMount() { this.load(); }

  load() {
    this.setState({ loading: true, error: false });
    Promise.all([fetchMissingZips(), fetchAbandonedCheckouts(), fetchBookingStarts()])
      .then(([zips, checkouts, starts]) => this.setState({ zips: zips || [], checkouts: checkouts || [], starts: starts || [], loading: false }))
      .catch(() => this.setState({ loading: false, error: true }));
  }

  matches(text) {
    const q = this.state.search.trim().toLowerCase();
    return !q || (text || "").toLowerCase().includes(q);
  }

  renderZips() {
    const { zipSort } = this.state;
    const rows = this.state.zips
      .filter(z => this.matches(`${z.zipcode} ${z.town} ${z.service}`))
      .sort((a, b) => zipSort === "recent" ? new Date(b.last_seen) - new Date(a.last_seen) : (b.searches - a.searches));
    const total = rows.reduce((n, z) => n + z.searches, 0);
    if (rows.length === 0) return <div style={styles.message}>No unserved zip searches yet.</div>;
    return <>
      <div style={styles.summary}><span><b>{rows.length}</b> zip/service combos</span><span><b>{total}</b> total searches</span></div>
      <div style={styles.scroll}><table style={styles.table}>
        <thead><tr>
          <th style={styles.th}>Zip</th><th style={styles.th}>Town</th><th style={styles.th}>Service</th>
          <th style={{ ...styles.th, ...styles.num }} onClick={() => this.setState({ zipSort: "searches" })}>Searches{zipSort === "searches" ? " ↓" : ""}</th>
          <th style={styles.th}>First seen</th>
          <th style={styles.th} onClick={() => this.setState({ zipSort: "recent" })}>Last seen{zipSort === "recent" ? " ↓" : ""}</th>
        </tr></thead>
        <tbody>{rows.map(z => <tr key={`${z.zipcode}-${z.service}`}>
          <td style={styles.td}><b>{z.zipcode}</b></td><td style={styles.td}>{z.town || "—"}</td><td style={styles.td}>{z.service}</td>
          <td style={{ ...styles.td, ...styles.num }}>{z.searches}</td><td style={styles.td}>{fmtDate(z.first_seen)}</td><td style={styles.td}>{fmtDate(z.last_seen)}</td>
        </tr>)}</tbody>
      </table></div>
    </>;
  }

  status(c) {
    if (c.booked) return <span style={styles.pill("#e6f6ec", "#1d7a43")}>Booked</span>;
    if (c.status === "accepted") return <span style={styles.pill("#e9eefc", "#3357ff")}>Reminder sent</span>;
    if (c.status === "withdrawn" || c.status === "suppressed") return <span style={styles.pill("#f1f1f4", "#687187")}>Opted out</span>;
    return <span style={styles.pill("#fff4e0", "#9a5b00")}>Abandoned</span>;
  }

  renderCheckouts() {
    const rows = this.state.checkouts
      .filter(c => !(this.state.hideBooked && c.booked))
      .filter(c => this.matches(`${c.email} ${c.address} ${(c.services || []).join(" ")}`));
    const value = rows.reduce((n, c) => n + (c.estimated_value || 0), 0);
    if (rows.length === 0) return <div style={styles.message}>No abandoned checkouts to show.</div>;
    return <>
      <div style={styles.summary}><span><b>{rows.length}</b> checkouts</span><span><b>{money(value)}</b> est. value</span></div>
      <div style={styles.scroll}><table style={{ ...styles.table, minWidth: "900px" }}>
        <thead><tr>
          <th style={styles.th}>Email</th><th style={styles.th}>Property</th><th style={styles.th}>Services</th>
          <th style={{ ...styles.th, ...styles.num }}>Est. value</th><th style={styles.th}>Last activity</th><th style={styles.th}>Status</th>
        </tr></thead>
        <tbody>{rows.map(c => <tr key={`${c.email}-${c.started_at}`}>
          <td style={styles.td}><a href={`mailto:${c.email}`}>{c.email}</a>{c.existing_customer && <div style={{ fontSize: "12px", color: "#687187" }}>Existing customer</div>}</td>
          <td style={styles.td}>{c.address || "—"}</td>
          <td style={styles.td}>{(c.services || []).length ? c.services.join(", ") : "—"}{c.coupon_code && <div style={{ fontSize: "12px", color: "#687187" }}>Code: {c.coupon_code}</div>}</td>
          <td style={{ ...styles.td, ...styles.num }}>{c.estimated_value ? money(c.estimated_value) : "—"}</td>
          <td style={styles.td}>{fmtDate(c.last_activity_at)}</td>
          <td style={styles.td}>{this.status(c)}</td>
        </tr>)}</tbody>
      </table></div>
    </>;
  }

  renderStarts() {
    const STEP_LABELS = { address: "Address", services: "Services", photographer: "Photographer / time", checkout: "Checkout", submitting: "Submitting", booked: "Booked" };
    const ORDER = ["address", "services", "photographer", "checkout", "submitting", "booked"];
    const all = this.state.starts.filter(b => this.matches(`${b.address} ${b.zipcode} ${b.source}`));
    const rows = all.filter(b => !(this.state.hideBookedStarts && b.booked));
    if (all.length === 0) return <div style={styles.message}>No started bookings yet. These appear once a logged-out visitor continues past the address step, and are kept for 90 days.</div>;
    const reached = ORDER.map(step => all.filter(b => b.booked || ORDER.indexOf(b.furthest_step) >= ORDER.indexOf(step)).length);
    return <>
      <div style={styles.summary}>
        {ORDER.filter(s => s !== "address" && s !== "submitting").map(step => {
          const i = ORDER.indexOf(step);
          return <span key={step}><b>{reached[i]}</b> reached {STEP_LABELS[step].toLowerCase()} ({all.length ? Math.round(reached[i] / all.length * 100) : 0}%)</span>;
        })}
        <span>of <b>{all.length}</b> started</span>
      </div>
      <div style={styles.scroll}><table style={{ ...styles.table, minWidth: "900px" }}>
        <thead><tr>
          <th style={styles.th}>Property</th><th style={styles.th}>Got to</th><th style={styles.th}>Services</th>
          <th style={{ ...styles.th, ...styles.num }}>Cart</th><th style={styles.th}>Source</th><th style={styles.th}>Device</th>
          <th style={styles.th}>Started</th><th style={styles.th}>Last activity</th><th style={styles.th}></th>
        </tr></thead>
        <tbody>{rows.map((b, i) => <tr key={`${b.started_at}-${i}`}>
          <td style={styles.td}>{b.address || "—"}</td>
          <td style={styles.td}>{b.booked ? <span style={styles.pill("#e6f6ec", "#1d7a43")}>Booked</span> : <span style={styles.pill("#fff4e0", "#9a5b00")}>{STEP_LABELS[b.furthest_step] || b.furthest_step}</span>}</td>
          <td style={styles.td}>{b.services || "—"}</td>
          <td style={{ ...styles.td, ...styles.num }}>{b.cart_total ? money(b.cart_total) : "—"}</td>
          <td style={styles.td}>{b.source}</td>
          <td style={styles.td}>{b.device || "—"}</td>
          <td style={styles.td}>{fmtDate(b.started_at)}</td>
          <td style={styles.td}>{fmtDate(b.last_activity_at)}{b.minutes_spent > 0 && <div style={{ fontSize: "12px", color: "#687187" }}>{b.minutes_spent} min in booking</div>}</td>
          <td style={styles.td}><a href="#" style={{ color: "#b42318", fontSize: "13px" }} onClick={e => { e.preventDefault(); if (window.confirm("Remove this started booking?")) deleteBookingStart(b.id).then(() => this.setState({ starts: this.state.starts.filter(x => x.id !== b.id) })); }}>Remove</a></td>
        </tr>)}</tbody>
      </table></div>
    </>;
  }

  render() {
    const { view, loading, error, search, hideBooked } = this.state;
    const btn = (key, label) => <button className={`settingsButton companyButton${view === key ? "" : " secondary"}`} style={{ ...styles.tab, opacity: view === key ? 1 : 0.6 }} onClick={() => this.setState({ view: key, search: "" })}>{label}</button>;
    return (
      <div className="settingsPreferencesGroupingDiv">
        <div className="settingsPreferences">
          <div className="settingsComponentHeading">Demand Insights</div>
          <div style={styles.tabs}>{btn("starts", "Started bookings")}{btn("zips", "Unserved zip codes")}{btn("checkouts", "Abandoned CO's")}</div>
          <div style={styles.filters}>
            <input className="formInput" style={{ maxWidth: "320px", margin: 0 }} placeholder={view === "zips" ? "Search zip, town or service" : view === "starts" ? "Search address, zip or source" : "Search email, address or service"} value={search} onChange={e => this.setState({ search: e.target.value })} />
            {view === "starts" && <label style={{ display: "flex", gap: "6px", alignItems: "center" }}><input type="checkbox" checked={this.state.hideBookedStarts} onChange={e => this.setState({ hideBookedStarts: e.target.checked })} /> Hide booked</label>}
            {view === "checkouts" && <label style={{ display: "flex", gap: "6px", alignItems: "center" }}><input type="checkbox" checked={hideBooked} onChange={e => this.setState({ hideBooked: e.target.checked })} /> Hide booked</label>}
          </div>
          {loading ? <div style={styles.message}>Loading...</div>
            : error ? <div style={styles.message}>Couldn't load this data. <a href="#" onClick={e => { e.preventDefault(); this.load(); }}>Try again</a></div>
            : view === "zips" ? this.renderZips() : view === "starts" ? this.renderStarts() : this.renderCheckouts()}
        </div>
      </div>
    );
  }
}
